import { COORDINATE_ROLE_LABELS, type CoordinateRole, type LegendaLang } from './vocabulary';

/**
 * Marks for the four kinds of "where".
 *
 * Confusing these is the highest-consequence failure available to this design —
 * a candidate position mistaken for a surveyed one becomes a real hole drilled
 * in the wrong ground — so they differ in *geometry*, not merely in colour:
 *
 * - `survey_certified`      filled triangle with a centre dot (a trig station).
 *                           The archive holds **none**. The symbol still appears
 *                           in every legend, empty, so the absence is visible
 *                           rather than merely unmentioned.
 * - `candidate_transformation`  ringed circle; the ring is drawn to map scale.
 * - `cross_document_orientation`  hollow diamond, and no ring — it is a point
 *                           borrowed from another document, not a measurement
 *                           of this hole.
 * - `raw_local`             square on a grid tick, and never plotted on WGS84.
 */
function RoleGlyph({ role }: { role: CoordinateRole }) {
  const common = { viewBox: '0 0 16 16', 'aria-hidden': true } as const;
  switch (role) {
    case 'survey_certified':
      return (
        <svg {...common}>
          <path d="M8 2 L14.5 13.5 H1.5 Z" fill="currentColor" />
          <circle cx="8" cy="10.2" r="1.5" fill="var(--lg-surface-raised)" />
        </svg>
      );
    case 'candidate_transformation':
      return (
        <svg {...common}>
          <circle cx="8" cy="8" r="3.1" fill="none" stroke="currentColor" strokeWidth="1.6" />
          <circle
            cx="8"
            cy="8"
            r="6.4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
            strokeDasharray="2.5 2.5"
          />
        </svg>
      );
    case 'cross_document_orientation':
      return (
        <svg {...common}>
          <path d="M8 1.8 L14.2 8 L8 14.2 L1.8 8 Z" fill="none" stroke="currentColor" strokeWidth="1.6" />
        </svg>
      );
    case 'raw_local':
      return (
        <svg {...common}>
          <path d="M8 1 V15 M1 8 H15" stroke="currentColor" strokeWidth="0.9" opacity="0.5" />
          <rect x="4.6" y="4.6" width="6.8" height="6.8" fill="none" stroke="currentColor" strokeWidth="1.6" />
        </svg>
      );
  }
}

export interface CoordinateRoleBadgeProps {
  role: CoordinateRole;
  lang: LegendaLang;
  /** Hide the text where the legend already carries it — map marks, dense rows. */
  glyphOnly?: boolean;
}

export default function CoordinateRoleBadge({ role, lang, glyphOnly = false }: CoordinateRoleBadgeProps) {
  const label = COORDINATE_ROLE_LABELS[role][lang];
  return (
    <span className={`lg-crole lg-crole--${role}`} aria-label={label} data-coordinate-role={role}>
      <RoleGlyph role={role} />
      {glyphOnly ? null : label}
    </span>
  );
}
